import { forwardRef, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { css, styled } from 'styled-components';

import { HeaderText, Paragraph, SmallText } from '../styles/text';
import { Button } from './Button';
import { ExitIcon } from './Icons';
import { CTALoader } from './Loader';

interface StyledDialogProps {
  onClose?: () => void;
}

const StyledDialog = styled.dialog<StyledDialogProps>(
  ({ theme }) => css`
    background-color: ${theme.colors.grayDarkest};
    border-radius: 12px;
    padding: 0;
    text-align: center;
    min-width: 337px;
    max-width: 510px;
    width: 90%;
    min-height: 300px;
    max-height: 95vh;
    overflow: hidden;

    &[open] {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    &::backdrop {
      background-color: rgb(0 0 0 / 50%);
    }
  `,
);

const CloseButton = styled.button(
  ({ theme }) => `
  background: none;
  margin: 0;
  padding: 0;
  border: 0;
  position: absolute;
  top: 15px;    
  right: 15px;
  z-index: 1;
  cursor: pointer;
  
  &:focus-visible {
    outline: 3px solid ${theme.colors.focusOutline}
  }
`,
);

const ScrollContainer = styled.div`
  position: relative;
  overflow-y: auto;
  width: 100%;
  max-height: 95vh;
  padding: 30px 20px 20px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  button + & {
    padding-top: 45px;
  }
`;

const ButtonContainer = styled.div`
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const TextContainer = styled.div`
  padding: 15px 25px;
`;

const PrimaryButton = styled(Button)(
  ({ theme }) => `
  color: ${theme.colors.grayDarkest};
  font-size: ${theme.fontSizes.medium};
  background: ${theme.colors.white};
`,
);

const SecondaryButton = styled(Button)(
  ({ theme }) => `
  color: ${theme.colors.grayLightest};
  font-size: ${theme.fontSizes.small};
  line-height: 21px;
  background: ${theme.colors.grayDarkest};
  margin-top: 10px;
`,
);

interface ButtonContent {
  text: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
}

export interface DialogProps {
  onCloseClick?: () => void;
  icon: ReactNode;
  title: ReactNode;
  textContents: ReactNode[];
  finePrint?: ReactNode[];
  primaryButton: ButtonContent;
  secondaryButton?: ButtonContent;
  children?: React.ReactNode;
}

export const Dialog = forwardRef<HTMLDialogElement, DialogProps>(
  (
    { onCloseClick, icon, title, textContents, finePrint, primaryButton, secondaryButton, children, ...dialogProps },
    ref,
  ) => {
    const intl = useIntl();

    return (
      <StyledDialog {...dialogProps} onClose={onCloseClick} ref={ref}>
        {onCloseClick && (
          <CloseButton onClick={onCloseClick} aria-label={intl.formatMessage({ id: 'membership-modal-close-button' })}>
            <ExitIcon aria-hidden="true" focusable="false" />
          </CloseButton>
        )}
        <ScrollContainer>
          {icon}
          <TextContainer>
            <HeaderText $textAlign="center" $fontSize="xxxlarge" $margin="0 0 25px">
              {title}
            </HeaderText>
            {textContents.map((text, index) => (
              <Paragraph $textAlign="center" $color="grayLightest" $lineHeight="22px" $margin="0 0 12px" key={index}>
                {text}
              </Paragraph>
            ))}
            {children}
            {finePrint && (
              <SmallText $textAlign="center" $color="grayLight" $fontSize="xsmall">
                {finePrint.map((part, index) => (
                  <span key={index}>{part}</span>
                ))}
              </SmallText>
            )}
          </TextContainer>

          <ButtonContainer>
            <PrimaryButton
              onClick={primaryButton.onClick}
              disabled={!!primaryButton.disabled}
              data-testid={primaryButton.testId}
            >
              {primaryButton.disabled ? <CTALoader /> : primaryButton.text}
            </PrimaryButton>
            {secondaryButton && (
              <SecondaryButton onClick={secondaryButton.onClick} data-testid={secondaryButton.testId}>
                {secondaryButton.disabled ? <CTALoader color="white" /> : secondaryButton.text}
              </SecondaryButton>
            )}
          </ButtonContainer>
        </ScrollContainer>
      </StyledDialog>
    );
  },
);
